import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../helpers/api_helper';
import { CashbackBonusData, CashbackStateType, CashbackType } from './types';
import { toast } from 'react-toastify';
import { handleValidDate, handleValidTime } from '../../../helpers/helpers';

export const getFastStartBonusList = createAsyncThunk(
  'getFastStartBonusList/fetch',
  async (params: URLSearchParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/bonus/cashback/user/list`, {
        params: {
          userId: params.get('userId'),
          countPerPage: '10'
        }
      });
      return response.data.data as CashbackBonusData;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const getFastStartBonus = createAsyncThunk(
  'getFastStartBonus/fetch',
  async (params: URLSearchParams, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/bonus/cashback/info`, {
        params: {
          userId: params.get('userId')
        }
      });
      return response.data.data as CashbackType;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const exportFastStartBonus = createAsyncThunk(
  'getFastStartBonus/exportFastStartBonus',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      '/bonus/cashback/user/list/export',
      {
        params
      }
    );

    try {
      if (response?.data?.data?.usersWithCashbackBonuses) {
        const cashBackBonuses =
          response?.data?.data?.usersWithCashbackBonuses[0]?.cashbackBonuses;
        const dataForExport = cashBackBonuses.map((u: any) => {
          return {
            id: u?.id,
            userPackageId: u?.userPackageId,
            amount: u?.amount,
            comissionAmount: u?.comissionAmount,
            cashbackBonusPercent: u?.cashbackBonusPercent,
            rewardPoints: u?.rewardPoints,
            shoppingPoints: u?.shoppingPoints,
            calculationStatus: u?.calculationStatus,
            status: statusForTable(u?.status),
            dateCreated: u?.dateCreated
              ? `${handleValidDate(u.dateCreated)}, ${handleValidTime(
                  u.dateCreated
                )}`
              : ''
          };
        });

        return dataForExport;
      }

      if (response.data.message) {
        toast.success(response.data.message);
      }
    } catch (error: any) {
      toast.error(error.response.data.error);
    }
  }
);

export const statusForTable = (status: number) => {
  switch (status) {
    case 1:
      return 'PAID';
    case 2:
      return 'INELIGIBLE';

    default:
      break;
  }
};

export const paramsForExport = (searchParams: URLSearchParams) => {
  const params = searchParams;
  params.delete('countPerPage');
  params.delete('numberOfPage');
  params.delete('sortBy');
  params.delete('sortOrder');
  return params;
};

export const exportHeaders = [
  { label: 'Bonus ID', key: 'id' },
  { label: 'Package ID', key: 'userPackageId' },
  { label: 'Amount', key: 'amount' },
  { label: 'Comission Amount', key: 'comissionAmount' },
  { label: 'Comission %', key: 'cashbackBonusPercent' },
  { label: 'Date Created', key: 'dateCreated' },
  { label: 'Reward Points', key: 'rewardPoints' },
  { label: 'Shopping Points', key: 'shoppingPoints' },
  { label: 'Status', key: 'status' }
];

const initialState = {
  data: {},
  list: {},
  dataForExport: [],
  loading: false,
  loadingExport: false
} as CashbackStateType;

const fastStartBonusSlice = createSlice({
  name: 'getCashbackBonus',
  initialState,
  reducers: {
    removeFastStartBonusForExport(state) {
      state.dataForExport = [];
    }
  },

  extraReducers: (builder) => {
    builder.addCase(getFastStartBonusList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFastStartBonusList.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.list = payload;
    });
    builder.addCase(getFastStartBonusList.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getFastStartBonus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getFastStartBonus.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.data = payload;
    });
    builder.addCase(getFastStartBonus.rejected, (state) => {
      state.loading = false;
    });

    //loading export
    builder.addCase(exportFastStartBonus.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportFastStartBonus.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportFastStartBonus.rejected, (state) => {
      state.loadingExport = false;
    });
  }
});

export const { removeFastStartBonusForExport } = fastStartBonusSlice.actions;
export default fastStartBonusSlice.reducer;
